import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";

import {
  Button,
  DropdownProps,
  Form,
  FormField,
  FormGroup,
  Icon,
  Input,
  InputProps,
  Modal,
} from "semantic-ui-react";

import moment from "moment";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import ModInfo from "react-lib/apps/common/ModInfo";
import PasswordRecheck from "react-lib/apps/common/PasswordRecheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

import CardRehabTreatment from "./CardRehabTreatment";
import CardSelectRehabUX from "./CardSelectRehabUX";
import { ColumnKeys } from "./TableTreatment";

import {
  CheckedType,
  LANGUAGE_OPTIONS,
  LanguageType,
  RehabPlanType,
  SelectRehabDeviceSlotType,
} from "./RHBInterface";

import { formatDate, getOrdinal } from "react-lib/utils/dateUtils";

type CardSelectRehabProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  rehabTreatmentList?: RehabPlanType[];
  selectedAppointment: any;
  selectedRehabDeviceSlot?: SelectRehabDeviceSlotType;
  selectedRehabItems?: CheckedType[];
  userProfile: any;
  buttonLoadCheck: any;
  errorMessage: any;
  languageUX: string;
  searchedItemListWithKey: any;
  selectedDivision: any;
  selectedPatient: any;
};

const BUTTON_ACTIONS = {
  cancel: "CANCEL",
  edit: "EDIT",
};

const HIDE_COLUMNS = ["create_plan", "repetition"] as ColumnKeys[];

const CARD_SELECT_REHAB = "CardSelectRehab";

const ACTION_CANCEL = `${CARD_SELECT_REHAB}_${BUTTON_ACTIONS.cancel}`;
const ACTION_EDIT = `${CARD_SELECT_REHAB}_${BUTTON_ACTIONS.edit}`;

const CardSelectRehab = (props: CardSelectRehabProps) => {
  const [isEditingAppointment, setIsEditingAppointment] = useState<boolean>(false);

  const [language, setLanguage] = useState<LanguageType>("TH");

  const [openModSelectRehab, setOpenModSelectRehab] = useState(false);
  const [modConfirmCancel, setModConfirmCancel] = useState<{
    note?: string;
    noTreatment?: boolean;
    password?: string;
  } | null>(null);

  const orderId = useMemo(() => {
    const orders: Record<string, any>[] = props.selectedAppointment?.orders || [];

    const foundOrder = orders.find((order) => order.type === "rehabilitationorder");

    return foundOrder?.id as number | undefined;
  }, [props.selectedAppointment?.id]);

  useEffect(() => {
    props.onEvent({ message: "getRhbTreatment", params: {} });

    props.onEvent({
      message: "getRhbAppointment",
      params: {
        appointment: props.selectedAppointment,
        card: CARD_SELECT_REHAB,
        orderId,
      },
    });

    return () => {
      // props.setProp(`buttonLoadCheck.${CARD_SELECT_REHAB}`, null);
    };
  }, [orderId]);

  useEffect(() => {
    setLanguage(props.languageUX.toUpperCase() as LanguageType);
  }, [props.languageUX]);

  const slotDate = useMemo(() => {
    const detail = props.selectedAppointment;

    return detail?.extra?.date && detail?.extra.start_time
      ? formatDate(moment(detail.extra.date))
      : "";
  }, [props.selectedAppointment]);

  const isLoadingSuccess = useMemo(
    () => props.buttonLoadCheck?.[CARD_SELECT_REHAB] === "SUCCESS",
    [props.buttonLoadCheck]
  );

  const isShowDetail = useMemo(() => !!slotDate && isLoadingSuccess, [isLoadingSuccess, slotDate]);

  const deviceList = useMemo(
    () =>
      (props.selectedRehabDeviceSlot?.devices ?? []).map((device) => ({
        end_datetime: `${device.end_date} ${device.end_time}`,
        name: device.name,
        start_datetime: `${device.start_date} ${device.start_time}`,
      })),
    [props.selectedRehabDeviceSlot]
  );

  const handleClickAddRehab = () => {
    setOpenModSelectRehab(true);
  };

  const handleCloseModSelectRehab = () => {
    setOpenModSelectRehab(false);
  };

  const handleSelectRehab = (checkedList: Record<string, any>) => {
    props.setProp("selectedRehabItems", checkedList);

    handleCloseModSelectRehab();
  };

  const handleEditAppointment = () => {
    setIsEditingAppointment((isEditing) => !isEditing);
  };

  const handleDeletePlanItem = (index: number) => () => {
    const checked = props.selectedRehabItems ?? [];

    checked.splice(index, 1);

    props.setProp("selectedRehabItems", [...checked]);
  };

  const handleOpenModConfirmCancel = () => {
    setModConfirmCancel({});
  };

  const handleCloseModConfirmCancel = () => {
    setModConfirmCancel(null);
  };

  const handleChangeNote = (e: SyntheticEvent, data: InputProps) => {
    setModConfirmCancel({ ...modConfirmCancel, note: String(data.value) });
  };

  const handleChangePassword = (password: string) => {
    setModConfirmCancel({ ...modConfirmCancel, password });
  };

  const handleConfirmCancel = () => {
    props.onEvent({
      message: "updateRhbTreatmentOrder",
      params: {
        action: "CANCEL",
        id: orderId,
        btnAction: ACTION_CANCEL,
        canceled_reason: modConfirmCancel?.note,
        card: CARD_SELECT_REHAB,
        password: modConfirmCancel?.password,
        onSuccess: () => {
          handleCloseModConfirmCancel();

          props.setProp("selectedAppointment", null);

          props.onEvent({ message: "RefreshAppointment", params: { noGetWaiting: true } });
        },
      },
    });
  };

  const handleConfirmEdit = () => {
    if (!props.selectedRehabItems?.length) {
      setModConfirmCancel({ noTreatment: true });
    }

    if (!props.selectedRehabDeviceSlot) {
      return;
    }

    const data = props.selectedRehabDeviceSlot;

    const selectedDevices = data.devices.map((device) => ({
      id: device.id,
      device: device.rehab_device_id,
      device_free: device.device_free,
      end_datetime: `${device.end_date} ${device.end_time}`,
      rehabilitation_order_id: device.rehabilitation_order_id,
      start_datetime: `${device.start_date} ${device.start_time}`,
    }));

    props.onEvent({
      message: "makeAppointmentRhbTreatment",
      params: {
        btnAction: ACTION_EDIT,
        devices: selectedDevices,
        division: props.selectedDivision?.id,
        items: props.selectedRehabItems,
        order_note: data.order_note,
        physiotherapist: data.physiotherapist,
        onSuccess: handleEditAppointment,
      },
    });
  };

  const handlePrintBooking = () => {
    props.onEvent({
      message: "PrintScheduling",
      params: {
        data: {
          language,
        },
        form: "FormPhysicalTherapyAppointment",
      },
    });
  };

  const handleChangeLanguage = (e: SyntheticEvent, data: DropdownProps) => {
    setLanguage(data.value as LanguageType);
  };

  return (
    <div>
      <SnackMessage
        error={props.errorMessage?.[CARD_SELECT_REHAB]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_SELECT_REHAB}`, null);
        }}
      />

      <CardSelectRehabUX
        isEditingAppointment={isEditingAppointment}
        isLoadingSuccess={isLoadingSuccess}
        isNotFound={(props.selectedRehabItems ?? []).length === 0}
        isShowDetail={isShowDetail}
        language={language}
        orderNote={props.selectedAppointment?.order_note}
        patientInfo={props.selectedPatient}
        physiotherapistName={props.selectedAppointment?.extra?.physiotherapist_fullname}
        slotDate={slotDate}
        languageUX={props.languageUX}
        languageOptions={LANGUAGE_OPTIONS}
        onAddRehab={handleClickAddRehab}
        onCancelAppointment={handleOpenModConfirmCancel}
        onChangeLanguage={handleChangeLanguage}
        onPrintBooking={handlePrintBooking}
        onToggleEditAppointment={handleEditAppointment}
        buttonConfirmEdit={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="orange"
            paramKey={ACTION_EDIT}
            size="small"
            title="CONFIRM EDITING"
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_EDIT]}
            onClick={handleConfirmEdit}
          />
        }
        deviceList={
          deviceList.length === 0 ? (
            "-"
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto max-content auto 1fr",
                lineHeight: "1.75",
                marginLeft: "0.5rem",
              }}
            >
              {deviceList.map((item, index) => (
                <React.Fragment key={`medication-${index}`}>
                  <div
                    style={{
                      backgroundColor: "rgba(0,0,0,.87)",
                      borderRadius: "500rem",
                      height: "4px",
                      margin: "auto",
                      width: "4px",
                    }}
                  />
                  <label style={{ padding: "0 5rem 0 0.5rem" }}>{item.name}</label>
                  <label style={{ paddingRight: "3rem" }}>Time</label>
                  <label>
                    {moment(item.start_datetime).format("HH:mm")} -{" "}
                    {moment(item.end_datetime).format("HH:mm")}
                  </label>
                </React.Fragment>
              ))}
            </div>
          )
        }
        rehabItems={
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              rowGap: "0.25rem",
            }}
          >
            {(props.selectedRehabItems ?? []).map((item, index) => {
              const ordinal = getOrdinal(item.no);

              return (
                <React.Fragment key={`rehab-item-${item.plan_item}`}>
                  <div>
                    {(isEditingAppointment || !isShowDetail) && (
                      <Icon color="red" name="minus circle" onClick={handleDeletePlanItem(index)} />
                    )}
                    <label style={{ padding: "0 3rem 0 0.25rem" }}>
                      {item.plan_item_detail?.name}
                    </label>
                  </div>
                  <label>
                    No. {ordinal.number}
                    <sup>{ordinal.suffix}</sup>
                  </label>
                </React.Fragment>
              );
            })}
          </div>
        }
      />

      <Modal
        style={{ lineHeight: "1.4285em", padding: "0 0.25rem 0.75rem" }}
        open={openModSelectRehab}
        closeOnDimmerClick
        onClose={handleCloseModSelectRehab}
      >
        <CardRehabTreatment
          onEvent={props.onEvent}
          setProp={props.setProp}
          checkedList={props.selectedRehabItems}
          hideColumns={HIDE_COLUMNS}
          isIncludePerformed={false}
          rehabTreatmentList={props.rehabTreatmentList}
          closeIcon
          hideEditDepartment
          hideFilter
          hideMode
          selectable
          errorMessage={props.errorMessage}
          languageUX={props.languageUX}
          searchedItemListWithKey={props.searchedItemListWithKey}
          selectedPatient={props.selectedPatient}
          onClose={handleCloseModSelectRehab}
          onSelectRehab={handleSelectRehab}
        />
      </Modal>

      <ModInfo
        style={{ width: "40rem" }}
        buttonStyle={{ display: "none" }}
        open={!!modConfirmCancel}
        titleColor="red"
        titleName="Alert message"
        onApprove={handleCloseModConfirmCancel}
        onClose={handleCloseModConfirmCancel}
      >
        <Form
          className="--override-disabled --grey"
          style={{ fontWeight: "bold", margin: "1.25rem 0 -0.5rem", textAlign: "left" }}
        >
          {modConfirmCancel?.noTreatment && (
            <FormGroup style={{ marginBottom: "0.75rem" }}>
              <FormField style={{ lineHeight: 1.75, textAlign: "center" }} width={16}>
                <div>The appointment will be canceled</div>
                <div>because there is no treatment plan requiring an appointment</div>
              </FormField>
            </FormGroup>
          )}
          <FormGroup style={{ marginBottom: "2rem" }}>
            <FormField width={16} required>
              <label style={{ fontWeight: "normal" }}>
                Please specify the reason for cancellation
              </label>
              <Input
                autoComplete="one-time-code"
                value={modConfirmCancel?.note ?? ""}
                fluid
                onChange={handleChangeNote}
              />
            </FormField>
          </FormGroup>
          <FormGroup inline>
            <FormField width={8} inline>
              <Input value={props.userProfile?.username ?? ""} disabled fluid />
            </FormField>
            <FormField width={8} inline>
              <PasswordRecheck
                password={modConfirmCancel?.password ?? ""}
                setPassword={handleChangePassword}
              />
            </FormField>
            <FormField inline>
              <ButtonLoadCheck
                setProp={props.setProp}
                color="green"
                paramKey={ACTION_CANCEL}
                size="medium"
                title="CONFIRM"
                basic
                buttonLoadCheck={props.buttonLoadCheck?.[ACTION_CANCEL]}
                onClick={handleConfirmCancel}
              />
            </FormField>
            <FormField style={{ paddingRight: 0 }} inline>
              <Button color="red" basic onClick={handleCloseModConfirmCancel}>
                CLOSE
              </Button>
            </FormField>
          </FormGroup>
        </Form>
      </ModInfo>
    </div>
  );
};

CardSelectRehab.displayName = "CardSelectRehab";

export default React.memo(CardSelectRehab);
